/**
 * Timezone Detection Toast Component
 * Shows a user-friendly notification when timezone is auto-detected
 */

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { MapPin, Clock, Settings, X } from "lucide-react"
import { getTimezoneDisplayName, formatTimeInTimezone } from "@/lib/timezone-utils"
import { useState } from "react"

interface TimezoneDetectionToastProps {
  detectedTimezone: string
  onChangeTimezone: () => void
  onDismiss: () => void
  className?: string
}

export function TimezoneDetectionToast({
  detectedTimezone,
  onChangeTimezone,
  onDismiss,
  className = ""
}: TimezoneDetectionToastProps) {
  const [isVisible, setIsVisible] = useState(true)

  const handleDismiss = () => {
    setIsVisible(false)
    setTimeout(onDismiss, 300) // Allow fade out animation
  }

  const timezoneDisplayName = getTimezoneDisplayName(detectedTimezone)
  const currentTime = formatTimeInTimezone(new Date(), detectedTimezone)

  if (!isVisible) {
    return null
  }

  return (
    <Card className={`max-w-md mx-auto shadow-lg border-primary/20 animate-in slide-in-from-top-2 duration-300 ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
            </div>
            
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium text-foreground">
                  Timezone Auto-Detected
                </h4>
                <Badge variant="secondary" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {currentTime}
                </Badge>
              </div>
              
              <p className="text-sm text-muted-foreground">
                We've set your timezone to{" "}
                <span className="font-medium text-foreground">{timezoneDisplayName}</span>.
                You can change this in your settings if needed.
              </p>
              
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onChangeTimezone}
                  className="h-8 text-xs"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Change Timezone
                </Button>
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                >
                  Got it
                </Button>
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default TimezoneDetectionToast